import {AfterViewInit, Component, ElementRef, EventEmitter, Injectable, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, GuardResult, MaybeAsync, Router, RouterStateSnapshot} from '@angular/router';
import {BehaviorSubject, EMPTY, of, Subscription} from 'rxjs';
import {catchError, filter, finalize, map, switchMap, take, tap} from 'rxjs/operators';

import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {MatFormFieldAppearance} from '@angular/material/form-field';

import {AssetUriService, ConfigService, LaunchDarklyFlag, LaunchDarklyService} from '@aiwyn/config';
import {AuthService, LogInAnonymouslyViaClientKeyParametersStrategy} from '@aiwyn/auth';

import {LoadableHelper, LoadableStatus, Logger} from '@aiwyn/shared';
import {CustomAnimation} from '../login-components/custom-animation';
import {MatDialog} from '@angular/material/dialog';
import {ModalAlertComponent} from '../../main/modals/alert/modal-alert.component';
import {ROUTE_TOKEN_LOGIN, ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_REGISTER} from '../../../routing-constants';

@Injectable({
	providedIn: 'root',
})
export class AddClientKeyContainerGuard implements CanActivate {

	constructor(
		private readonly authService: AuthService,
		private readonly router: Router,
	) { }

	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): MaybeAsync<GuardResult> {
    return this.authService.getAuthProviderToken().pipe(
      filter(({ status }) => LoadableHelper.isLoaded(status, true)),
      switchMap(({ status, data: token }) => {
        if (!token) {
          return of(true);
        }

        return this.authService.getUserLoadable().pipe(
          filter(({ status }) => LoadableHelper.isLoaded(status, true)),
          map(({ status, data: user }) => {
            if (status === LoadableStatus.ERROR) {
              return this.router.createUrlTree([ROUTE_TOKEN_LOGIN]);
            }

            if (!user) {
              return true;
            }

            // If user is anonymous, redirect them to main. If user is known, they can add a client id
            return user.isAnonymous() ? this.router.createUrlTree([ROUTE_TOKEN_MAIN_BASE]) : true
          })
        )
      }),
    );
	}
}

@Component({
  selector: 'app-client-key',
  templateUrl: './client-key.component.html',
  styleUrls: ['./client-key.component.scss'],
  animations: CustomAnimation,
  standalone: false,
})
export class ClientKeyComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() appearance: MatFormFieldAppearance;
  @Input() registrationEnabled = true;
  @Input() dontHaveAnAccountText = 'Not signed up?';
  @Input() createAccountButtonText = 'Create an account';
  @Output() onCreateAccountRequested: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('clientKey')
  clientKeyInput: ElementRef;

  @ViewChild('invoiceNumber')
  invoiceNoInput: ElementRef;

  @ViewChild('clientName')
  clientNameInput: ElementRef;

  private authStateSubscription: Subscription;
  clientKeyForm: UntypedFormGroup;
  displayName: string = '!';
  isAnonymousUser = true;
  clientKeyLabel: string = '';
  clientKeyInstructionText: string = '';

  enableMagicLinkExperience$ = this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_PASSWORDLESS_LOGIN);

  constructor(
    public authService: AuthService,
    public assetsUriService: AssetUriService,
    public configService: ConfigService,
    public launchDarklyService: LaunchDarklyService,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private readonly logger: Logger,
	  private readonly dialog: MatDialog,
    private readonly clientKeyLoginStrategy: LogInAnonymouslyViaClientKeyParametersStrategy
  ) { }

  ngAfterViewInit(): void {
    this.clientKeyInput.nativeElement.focus();
  }

  ngOnInit(): void {
		this.authService.getAuthProviderToken().pipe(
      filter(({ status }) => LoadableHelper.isLoaded(status, true)),
			take(1),
      switchMap(({ status, data: token }) => {
        if (status === LoadableStatus.ERROR || !token) {
          // User is completely unauthenticated
          this.displayName = ' back!';
          return EMPTY;
        }

        // User might be authenticated or in an in-between state
        return this.authService.getUser(true).pipe(
          take(1),
          switchMap(user => {
            // If the contact user has clients and we can bypass, do so
            if (user?.getClientIdList().length > 0 && this.configService.bypassClientKey) {
              return this.router.navigate([ROUTE_TOKEN_MAIN_BASE]);
            }

            // Prepopulate form
            this.displayName = ' back!';
            // User is known, update display name
            if (user && !user.isAnonymous()) {
              this.isAnonymousUser = false;
              this.displayName = `, ${user.getDisplayName()}!`;
            }

            return EMPTY;
          }),
        );
      }),
    ).subscribe();

    // get client key text for display fields
    this.clientKeyLabel = this.configService.clientKeyLabel;
    this.clientKeyInstructionText = this.configService.clientKeyInstructionsText || 'Enter your client key below';

    this.clientKeyForm = this.getFormBuilder();
  }

  getFormBuilder() {
    if (this.configService.relaxedModeEnabled) {
      return this.formBuilder.group({
        clientName: ['', [Validators.required]],
        clientKey: ['', [Validators.required]]
      });
    } else {
      return this.formBuilder.group({
        invoiceNumber: [''],
        clientKey: ['']
      });
    }
  }

  ngOnDestroy(): void {
    if (this.authStateSubscription && !this.authStateSubscription.closed) {
      this.authStateSubscription.unsubscribe();
      this.authStateSubscription = null;
    }
  }

  isSubmissionProcessing$ = new BehaviorSubject<boolean>(false);

	/**
	 * If the user is signed in and has a clientId, then let them go to main, otherwise they need to log in
	 */
  goBack(): void {
    this.authService.getAuthProviderToken().pipe(
      filter(({ status }) => LoadableHelper.isLoaded(status, true)),
      take(1),
      switchMap(({ status, data: token }) => {
        if (status !== LoadableStatus.LOADED || !token) {
          return this.router.navigate([ROUTE_TOKEN_LOGIN]);
        }

        return this.authService.getUser(true).pipe(
          take(1),
          map(user => user?.getClientIdList().length > 0),
          switchMap(redirectToMain => this.router.navigate(redirectToMain ? [ROUTE_TOKEN_MAIN_BASE] : [ROUTE_TOKEN_LOGIN])),
        );
      }),
      take(1),
    ).subscribe();
  }

  createAccount(): void {
    this.router.navigate([ROUTE_TOKEN_REGISTER]);
  }

  submit(): void {
    this.isSubmissionProcessing$.next(true);

    this.authService.clientKeyPostProcessing(
      this.authService.getAuthProviderToken().pipe(
        filter(({ status }) => LoadableHelper.isLoaded(status, true)),
        take(1),
        tap(({ status }) => {
          if (status === LoadableStatus.ERROR) {
            throw new Error('Failed to determine authentication');
          }
        }),
        switchMap(({ data: user }) => {
          // User attempting to sign in anonymously with a client key
          if (!user) {
            return this.authService.logIn(
              this.clientKeyLoginStrategy,
              {
                clientKey: this.clientKeyForm.controls.clientKey.value,
                invoiceNumber: this.clientKeyForm.controls.invoiceNumber.value,
              },
            );
          }

          // User is known, and attempting to add a client to their account
          return this.configService.relaxedModeEnabled ?
            // When relaxed mode is enabled, client can be created on the fly.
            this.authService.addClientToUserWithRelaxEnabled(
              this.clientKeyForm.controls.clientKey.value,
              this.clientKeyForm.controls.clientName.value,
            ) :
            // When relaxed mode is not enabled, client key and invoice number are required.
            this.authService.addClientToUser(
              this.clientKeyForm.controls.clientKey.value,
              this.clientKeyForm.controls.invoiceNumber.value,
            );
        }),
      )
    ).pipe(
      // THIS STAYS ABOVE THE CATCH ERROR!!! Navigate back to main, if there was a redirect, it will be handled there
      switchMap(_ => this.router.navigate([ROUTE_TOKEN_MAIN_BASE])),
      catchError(error => {
        this.logger.warn(
          'ClientKeyComponent :: Submit :: Failed to either add client to existing user, or log in anonymously with client key',
          error,
        );
        this.showModalAlert();
        throw error;
      }),
      take(1),
      finalize(() => this.isSubmissionProcessing$.next(false)),
    ).subscribe();
  }

  private showModalAlert() {
	  return this.dialog.open(
			ModalAlertComponent,
		  { data: { title: 'Client Key Error', message: 'The client key entered is either not valid or the client is inactive.' } }
	  );
  }
}
